import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { IMetaField } from '../../types/meta-fields-types';
import { FieldInputV2 } from '../FieldInput/FieldInputV2';
import { RANGE_META_FIELD } from '../MetaFieldsV2/constants/common';

type DraftMetaField = Pick<IMetaField, 'key' | 'value'>;

interface DraftMetaFieldsProps {
  metaFields: DraftMetaField[];
  onChange: (list: DraftMetaField[]) => void;
  title: string;
}

const EMPTY_FIELD: DraftMetaField = { key: '', value: '' };

const validateField = (field: DraftMetaField): string | null => {
  if (!field.key.trim() || !field.value.trim()) {
    return 'Key and value are required';
  }

  if (field.key.length < RANGE_META_FIELD.MIN || field.key.length > RANGE_META_FIELD.MAX) {
    return `Key must be between ${RANGE_META_FIELD.MIN} and ${RANGE_META_FIELD.MAX} characters`;
  }

  if (field.value.length < RANGE_META_FIELD.MIN || field.value.length > RANGE_META_FIELD.MAX) {
    return `Value must be between ${RANGE_META_FIELD.MIN} and ${RANGE_META_FIELD.MAX} characters`;
  }

  return null;
};

const DraftMetaFields: React.FC<DraftMetaFieldsProps> = ({ metaFields, onChange, title }) => {
  const { t } = useTranslation();
  const [newField, setNewField] = useState<DraftMetaField>(EMPTY_FIELD);

  const updateField = (index: number, updatedField: DraftMetaField) => {
    const list = metaFields.map((field, i) => (i === index ? updatedField : field));
    onChange(list);
  };

  const removeField = (index: number) => {
    onChange(metaFields.filter((_, i) => i !== index));
  };

  const addField = () => {
    const error = validateField(newField);

    if (error) {
      toast.error(error);
      return;
    }

    if (metaFields.some((field) => field.key === newField.key.trim())) {
      toast.error('A field with this key already exists');
      return;
    }

    onChange([...metaFields, { key: newField.key.trim(), value: newField.value.trim() }]);
    setNewField(EMPTY_FIELD);
  };

  return (
    <div className="c-meta-field c-meta-field_v2 mt-20 rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
      <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
        <h3 className="font-medium text-black dark:text-white">{title}</h3>
      </div>
      <div className="flex flex-col gap-5.5 p-7">
        {metaFields.length === 0 ? (
          <p className="text-gray-500 text-sm">{t('labels.noMetaFields')}</p>
        ) : (
          metaFields.map((field, index) => (
            <FieldInputV2
              key={index}
              field={field}
              onChange={(updatedField) => updateField(index, updatedField)}
              onRemove={() => removeField(index)}
            />
          ))
        )}

        <div className="mt-7.5">
          <h3 className="mb-4 text-lg font-medium text-black dark:text-white">{t('labels.addNewMetaField')}</h3>
          <FieldInputV2
            field={newField}
            onChange={setNewField}
          />
          <button
            type="button"
            onClick={addField}
            className="inline-flex items-center justify-center rounded-md bg-secondary px-6 py-2 mt-4 text-white hover:bg-opacity-90 disabled:opacity-50"
          >
            {t('buttons.add')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DraftMetaFields;
